import { useState, useEffect, useRef } from 'react';
import API, { formatError } from '../api/client';
import { Plus, Printer, Trash2, X, FileText } from 'lucide-react';
import PatientSelect from '../components/PatientSelect';
import MedicationAutocomplete from '../components/MedicationAutocomplete';
import CaduceusIcon from '../components/CaduceusIcon';
import { useAuth } from '../context/AuthContext';

const EMPTY_FORM = {
  patient_id: '', date: new Date().toISOString().split('T')[0],
  medicaments: [], notes: '', instructions: '', duree_traitement: ''
};
const EMPTY_MED = { nom: '', dosage: '', posologie: '', duree: '' };

// Médecin de l'en-tête d'ordonnance : le prescripteur enregistré s'il existe,
// sinon le médecin connecté (les ordonnances importées n'ont pas de médecin lié).
function printDoctorInfo(printPresc, user) {
  const fromUser = user && user !== false ? {
    nom: user.nom, specialite: user.specialite, cabinet: user.cabinet,
    adresse: user.adresse_cabinet, telephone: user.telephone_cabinet,
    ville: user.ville, inpe: user.inpe, nom_ar: user.nom_ar,
    titres_fr: user.titres_fr, titres_ar: user.titres_ar, ice: user.ice,
  } : {};
  return (printPresc?.medecin_info && printPresc.medecin_info.nom) ? printPresc.medecin_info : fromUser;
}

export default function PrescriptionPage() {
  const { user } = useAuth();
  const [prescriptions, setPrescriptions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [printPresc, setPrintPresc] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM, medicaments: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef();

  const load = async () => {
    const [p, pt] = await Promise.all([API.get('/prescriptions'), API.get('/patients')]);
    setPrescriptions(p.data);
    setPatients(pt.data.data ?? pt.data);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ ...EMPTY_FORM, medicaments: [{ ...EMPTY_MED }] });
    setError('');
    setShowModal(true);
  };

  const addMed = () => setForm(f => ({ ...f, medicaments: [...f.medicaments, { ...EMPTY_MED }] }));
  const removeMed = (i) => setForm(f => ({ ...f, medicaments: f.medicaments.filter((_, idx) => idx !== i) }));
  const updateMed = (i, field, val) => setForm(f => ({
    ...f,
    medicaments: f.medicaments.map((m, idx) => idx === i ? { ...m, [field]: val } : m)
  }));

  const handleSave = async () => {
    setError('');
    if (!form.patient_id || form.medicaments.length === 0 || !form.medicaments[0].nom) {
      setError('Veuillez sélectionner un patient et ajouter au moins un médicament.');
      return;
    }
    setSaving(true);
    try {
      await API.post('/prescriptions', form);
      setShowModal(false);
      load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette ordonnance ?')) return;
    await API.delete(`/prescriptions/${id}`);
    load();
  };

  const handlePrint = async (id) => {
    const res = await API.get(`/prescriptions/${id}`);
    setPrintPresc(res.data);
    setTimeout(() => window.print(), 300);
  };

  const F = ({ label, children }) => (
    <div className="flex flex-col space-y-1">
      <label className="text-xs font-medium text-stone-600">{label}</label>
      {children}
    </div>
  );

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="prescriptions-page">
      {/* Print area (hidden until print) */}
      {printPresc && (
        <div className="print-only" ref={printRef}>
          <div className="rx-sheet" style={{ fontFamily: 'Arial, sans-serif', padding: '12mm 12mm 8mm', minHeight: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            {/* ── En-tête compact (FR / AR) ── */}
            {(() => {
              const M = printDoctorInfo(printPresc, user);
              const titresFr = String(M.titres_fr || M.specialite || 'Médecin Généraliste').split('\n').map(s => s.trim()).filter(Boolean);
              const titresAr = String(M.titres_ar || '').split('\n').map(s => s.trim()).filter(Boolean);
              const hasAr = M.nom_ar || titresAr.length > 0;
              const line = { margin: '0.5px 0', fontSize: '9.5px', color: '#57534e', lineHeight: 1.25 };
              const name = { margin: 0, color: '#059669', fontSize: '14px', fontWeight: 700, lineHeight: 1.2 };
              return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'flex-start', borderBottom: '2px solid #059669', paddingBottom: '5px' }}>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <h2 style={name}>{M.nom || 'Médecin'}</h2>
                      {titresFr.map((t, i) => <p key={i} style={line}>{t}</p>)}
                      {M.ice && <p style={{ ...line, marginTop: '2px' }}>ICE: {M.ice}</p>}
                    </div>
                    {/* Logo (caducée) centré entre les deux en-têtes */}
                    <div style={{ flexShrink: 0, alignSelf: 'center', padding: '0 6px' }}>
                      <CaduceusIcon size={46} color="#059669" />
                    </div>
                    {hasAr && (
                      <div style={{ flex: 1, textAlign: 'right', direction: 'rtl' }}>
                        {M.nom_ar && <h2 style={name}>{M.nom_ar}</h2>}
                        {titresAr.map((t, i) => <p key={i} style={line}>{t}</p>)}
                        {M.ice && <p style={{ ...line, marginTop: '2px' }}>ICE: {M.ice}</p>}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '12px', color: '#374151' }}>
                    <span>{M.ville ? `${M.ville}, le : ` : 'Le : '}<b>{printPresc.date}</b></span>
                    {hasAr && M.ville && <span style={{ direction: 'rtl' }}>{M.ville}، في : <b>{printPresc.date}</b></span>}
                  </div>
                </div>
              );
            })()}

            {/* ── Contenu ── */}
            <div style={{ marginTop: '14px' }}>
              <p style={{ margin: 0, fontSize: '13px' }}>
                <span style={{ color: '#374151', fontWeight: 600 }}>Patient : </span>
                <b>{printPresc.patient_info?.prenom} {printPresc.patient_info?.nom}</b>
                {printPresc.patient_info?.date_naissance && ` — Né(e) le ${printPresc.patient_info.date_naissance}`}
                {printPresc.patient_info?.CIN && ` — CIN: ${printPresc.patient_info.CIN}`}
              </p>

              <h3 style={{ fontSize: '15px', color: '#059669', borderBottom: '1px solid #d1fae5', paddingBottom: '5px', margin: '12px 0 12px' }}>
                ORDONNANCE
              </h3>

              <ol style={{ paddingLeft: '20px', margin: 0 }}>
                {printPresc.medicaments?.map((m, i) => (
                  <li key={i} style={{ marginBottom: '10px', fontSize: '14px' }}>
                    <b>{m.nom}</b>
                    {m.dosage && ` — ${m.dosage}`}
                    {m.posologie && <p style={{ margin: '2px 0 0 0', color: '#57534e', fontSize: '13px' }}>Posologie: {m.posologie}</p>}
                    {m.duree && <p style={{ margin: '1px 0 0 0', color: '#57534e', fontSize: '13px' }}>Durée: {m.duree}</p>}
                  </li>
                ))}
              </ol>

              {printPresc.instructions && (
                <div style={{ marginTop: '14px', padding: '10px', background: '#f0fdf4', borderRadius: '6px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#374151' }}><b>Instructions:</b> {printPresc.instructions}</p>
                </div>
              )}
            </div>

            {/* ── Espace extensible : pousse signature + pied en bas de page ── */}
            <div style={{ flexGrow: 1, minHeight: '30px' }} />

            {/* ── Bas de page : signature (avec place pour le cachet) + coordonnées ── */}
            <div>
              <div style={{ borderTop: '1px dashed #d1d5db', paddingTop: '8px', textAlign: 'right', minHeight: '70px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#374151' }}>Cachet et signature du médecin</p>
              </div>
              {(() => {
                const M = printDoctorInfo(printPresc, user);
                if (!(M.telephone || M.adresse || M.cabinet)) return null;
                return (
                  <div style={{ marginTop: '6px', borderTop: '1px solid #d1fae5', paddingTop: '6px', textAlign: 'center', fontSize: '10.5px', color: '#78716c' }}>
                    {[M.cabinet, M.adresse].filter(Boolean).join(' — ')}
                    {M.telephone && <span> {(M.cabinet || M.adresse) ? '·' : ''} Tél : {M.telephone}</span>}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="no-print">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-stone-900">Ordonnances</h1>
            <p className="text-stone-500 text-sm mt-0.5">{prescriptions.length} ordonnance(s)</p>
          </div>
          <button data-testid="new-prescription-button" onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors">
            <Plus size={17} /> Nouvelle Ordonnance
          </button>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <table className="w-full" data-testid="prescriptions-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {['Date', 'Patient', 'Médicaments', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {prescriptions.length === 0 && (
                <tr><td colSpan={4} className="text-center py-10 text-stone-400">
                  <FileText size={36} className="text-stone-200 mx-auto mb-2" />
                  <p className="text-sm">Aucune ordonnance</p>
                </td></tr>
              )}
              {prescriptions.map(p => (
                <tr key={p.id} className="hover:bg-stone-50/60 transition-colors" data-testid="prescription-row">
                  <td className="px-4 py-3 text-sm font-medium text-stone-800">{p.date}</td>
                  <td className="px-4 py-3 text-sm text-stone-700">{p.patient_nom || '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">
                    {p.medicaments?.slice(0, 2).map((m, i) => <span key={i} className="block truncate">{m.nom}</span>)}
                    {p.medicaments?.length > 2 && <span className="text-xs text-stone-400">+{p.medicaments.length - 2} autres</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => handlePrint(p.id)} data-testid="print-prescription-btn"
                        className="p-1.5 text-stone-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" title="Imprimer">
                        <Printer size={16} />
                      </button>
                      <button onClick={() => handleDelete(p.id)}
                        className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Supprimer">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 no-print">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prescription-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
              <h2 className="font-heading font-semibold text-stone-800 text-lg">Nouvelle Ordonnance</h2>
              <button onClick={() => setShowModal(false)} className="p-1 text-stone-400 hover:text-stone-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{error}</div>}
              <div className="grid grid-cols-2 gap-4">
                <F label="Patient *">
                  <PatientSelect
                    value={form.patient_id}
                    patients={patients}
                    onChange={(id) => setForm(f => ({ ...f, patient_id: id }))}
                  />
                </F>
                <F label="Date">
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
                </F>
              </div>

              {/* Medications */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-stone-700">Médicaments *</label>
                  <button onClick={addMed} data-testid="add-medication-button"
                    className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium border border-emerald-200 hover:bg-emerald-50 px-2.5 py-1.5 rounded-md transition-colors">
                    <Plus size={14} /> Ajouter
                  </button>
                </div>
                <div className="space-y-3">
                  {form.medicaments.map((m, i) => (
                    <div key={i} className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <MedicationAutocomplete
                            className="col-span-2"
                            inputClassName="w-full px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                            placeholder="Nom du médicament *"
                            value={m.nom}
                            onChange={nom => updateMed(i, 'nom', nom)}
                            onPick={med => {
                              updateMed(i, 'nom', med.nom);
                              if (!m.dosage) updateMed(i, 'dosage', [med.dosage, med.unite_dosage].filter(Boolean).join(' '));
                            }}
                          />
                          <input placeholder="Dosage (ex: 500mg)" value={m.dosage}
                            onChange={e => updateMed(i, 'dosage', e.target.value)}
                            className="px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
                          <input placeholder="Durée (ex: 7 jours)" value={m.duree}
                            onChange={e => updateMed(i, 'duree', e.target.value)}
                            className="px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
                          <input placeholder="Posologie (ex: 1cp 3x/jour après repas)" value={m.posologie}
                            onChange={e => updateMed(i, 'posologie', e.target.value)}
                            className="px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white col-span-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
                        </div>
                        {form.medicaments.length > 1 && (
                          <button onClick={() => removeMed(i)} className="p-1 text-stone-400 hover:text-red-500 mt-0.5">
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <F label="Instructions pour le patient">
                <textarea rows={2} placeholder="Conseils, régime alimentaire, suivi..." value={form.instructions}
                  onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
              <button onClick={() => setShowModal(false)} className="px-5 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-100">Annuler</button>
              <button data-testid="save-prescription-button" onClick={handleSave} disabled={saving}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
